// src/web/client/src/pages/BillingSuccess.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Package, BarChart3, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [planDetails, setPlanDetails] = useState(null);
  
  const plan = searchParams.get('plan') || 'pro';
  const isDemo = searchParams.get('demo') === 'true';
  
  const planFeatures = {
    basic: {
      name: 'Basic Plan',
      price: 29,
      color: 'blue',
      features: [
        { icon: MessageSquare, text: 'Up to 1,000 messages per month' },
        { icon: Package, text: '5 message templates' },
        { icon: BarChart3, text: 'Basic analytics' }
      ]
    },
    pro: {
      name: 'Pro Plan',
      price: 49,
      color: 'indigo',
      features: [
        { icon: MessageSquare, text: 'Unlimited messages' },
        { icon: Package, text: 'Unlimited templates' },
        { icon: BarChart3, text: 'Advanced analytics & insights' }
      ]
    }
  };
  
  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setPlanDetails(planFeatures[plan] || planFeatures.pro);
      setIsLoading(false);
    }, 1500);
  }, [plan]);
  
  const handleContinue = () => {
    navigate('/dashboard');
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Activating your subscription...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isDemo ? 'Demo Mode Activated!' : 'Subscription Activated!'}
          </h1>
          <p className="text-gray-600">
            {isDemo 
              ? 'You can now explore all Pro features in demo mode'
              : `Welcome to Ecomoria ${planDetails?.name}! Your subscription is now active.`
            }
          </p>
        </div>
        
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{planDetails?.name}</CardTitle>
                <CardDescription>
                  {isDemo ? 'Demo Mode - No charges' : `$${planDetails?.price}/month`}
                </CardDescription>
              </div>
              <Badge className={`bg-${planDetails?.color}-100 text-${planDetails?.color}-800`}>
                Active
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <h3 className="font-medium mb-4">Your plan includes:</h3>
            <div className="space-y-3">
              {planDetails?.features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-gray-600" />
                    <span className="text-gray-700">{feature.text}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-blue-900 mb-2">🚀 Next Steps</h3>
          <ol className="space-y-2 text-blue-800">
            <li>1. Set up your first message template</li>
            <li>2. Configure your store settings</li>
            <li>3. Send a test message to verify everything works</li>
            <li>4. Watch your customer engagement soar!</li>
          </ol>
        </div>
        
        <div className="flex justify-center">
          <Button 
            onClick={handleContinue}
            size="lg"
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            Continue to Dashboard
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}